import gsap from 'gsap';
import { createStableResizeHandler } from '../viewport-utils';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

export default function initAnimation(
    root: HTMLDivElement,
    bgCanvas: HTMLCanvasElement,
) {
    const frameCount = 650;
    let animation: gsap.core.Tween | null = null;
    let disposed = false;

    bgCanvas.width = root.clientWidth;
    bgCanvas.height = root.clientHeight;
    const context = bgCanvas.getContext('2d')!;

    // 生成对应帧的远程地址
    const currentFrame = (index: number) =>
        getStaticAssetUrl(
            `/generate_video/${index.toString().padStart(5, '0')}.jpg`,
        );

    // 用于存储加载完成的图片，如果某帧加载失败则为 null
    const images: (HTMLImageElement | null)[] = new Array(frameCount).fill(
        null,
    );

    // 用于 GSAP 动画的对象
    const bgs = { frame: 0 };

    /**
     * 预加载某个区间 [start, end) 的帧
     * @param start 起始下标（含）
     * @param end   结束下标（不含）
     * @returns Promise<void[]> 代表这批帧全部加载完（或失败）后会 resolve
     */
    function preloadImagesChunk(start: number, end: number) {
        const chunkPromises: Promise<void>[] = [];
        for (let i = start; i < end && i < frameCount; i++) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = currentFrame(i);

            const promise = new Promise<void>((resolve) => {
                img.onload = () => {
                    images[i] = img;
                    resolve();
                };
                img.onerror = () => {
                    console.error(`Failed to load image: ${img.src}`);
                    images[i] = null;
                    resolve();
                };
            });
            chunkPromises.push(promise);
        }
        return Promise.all(chunkPromises);
    }

    /**
     * 主预加载函数
     * 思路：
     *  1) 先加载前 5 帧
     *  2) 启动动画
     *  3) 分批加载剩余帧
     */
    function preloadImages() {
        // 1) 先加载前 5 帧
        preloadImagesChunk(0, 5)
            .then(() => {
                if (disposed) return;

                // 2) 加载完前 5 帧后，启动动画
                startAnimation();

                // 3) 继续分批懒加载剩余帧，这里每次加载 20 帧
                let startIndex = 5;
                const chunkSize = 20;

                const loadNextChunk = () => {
                    if (disposed) return;
                    if (startIndex >= frameCount) return;

                    const end = Math.min(startIndex + chunkSize, frameCount);
                    preloadImagesChunk(startIndex, end).finally(() => {
                        if (disposed) return;
                        startIndex += chunkSize;
                        loadNextChunk(); // 递归式加载
                    });
                };
                loadNextChunk();
            })
            .catch((error) => {
                console.error(error);
            });
    }

    /**
     * 启动动画
     */
    function startAnimation() {
        animation = gsap.to(bgs, {
            frame: frameCount - 1,
            snap: 'frame',
            ease: 'none',
            repeat: -1, // 循环播放
            duration: 22,
            onUpdate: render,
        });
        render(); // 初始渲染
    }

    /**
     * 负责绘制当前帧
     */
    function render() {
        // 若 canvas 大小和 root 不一致，就重设
        if (
            bgCanvas.width !== root.clientWidth ||
            bgCanvas.height !== root.clientHeight
        ) {
            bgCanvas.width = root.clientWidth;
            bgCanvas.height = root.clientHeight;
        }

        const frame = Math.floor(bgs.frame);
        const img = images[frame];

        // 如果当前帧未加载完成，可以选择：
        // 1) 直接 return，不绘制，画面会卡一帧
        // 2) 用上一帧或占位图替代，这里仅做简单处理
        if (!img || !img.complete || img.naturalWidth === 0) {
            return;
        }

        const canvasRatio = bgCanvas.width / bgCanvas.height;
        const imgRatio = img.naturalWidth / img.naturalHeight;
        let sx = 0,
            sy = 0;
        let sWidth = img.naturalWidth,
            sHeight = img.naturalHeight;

        if (imgRatio > canvasRatio) {
            // 图片比画布更宽时：裁切左右
            sWidth = img.naturalHeight * canvasRatio;
            sHeight = img.naturalHeight;
            sx = (img.naturalWidth - sWidth) / 2;
        } else {
            // 图片比画布更高时：裁切上下
            sWidth = img.naturalWidth;
            sHeight = img.naturalWidth / canvasRatio;
            sy = (img.naturalHeight - sHeight) / 2;
        }

        context.clearRect(0, 0, bgCanvas.width, bgCanvas.height);
        context.drawImage(
            img,
            sx,
            sy,
            sWidth,
            sHeight,
            0,
            0,
            bgCanvas.width,
            bgCanvas.height,
        );
    }

    // 启动主预加载逻辑
    preloadImages();

    // 监听窗口大小变化，重新绘制
    const handleResize = createStableResizeHandler(() => {
        bgCanvas.width = root.clientWidth;
        bgCanvas.height = root.clientHeight;
        render();
    });
    window.addEventListener('resize', handleResize);

    return () => {
        disposed = true;
        window.removeEventListener('resize', handleResize);
        animation?.kill();
    };
}
